import { teamMembers, WHATSAPP_URL } from '../data/siteData';
import { PageHero, CTASection } from '../components/UI';

// Per-member photo focus point — keeps faces visible
const photoFocus = {
  'Hammad Khalid':     'center 15%',   // face is upper-center
  'Muhammad Afnan':    'center 20%',   // face upper-center, tall shot
  'Hasnaat Khalid':    'center 18%',   // office shot, face upper-center with Travizon sign
  'Saleha Shahbaz':    'center 25%',   // slightly lower to show full face
  'Momna Akram':       'center 20%',   // full body, face upper third
  'Imran Hameed':      'center 20%',   // office desk shot, face upper-center
};

export default function Team({ setPage }) {
  return (
    <div>
      <PageHero
        breadcrumb="Our Team"
        title="The People Behind Your Journey"
        subtitle="Every consultant at Travizon has personal experience navigating international education or immigration. We guide you because we've been there."
      />

      {/* ── TEAM GRID ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="section-tag">Meet the Team</span>
            <h2 className="font-display text-4xl sm:text-5xl text-navy font-semibold mt-1 mb-3">Experts Who Care</h2>
            <p className="text-gray-500 max-w-lg mx-auto">Not call-centre agents. Not interns. Qualified consultants who treat your case like it's their own.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <div
                key={member.name}
                className="bg-white border border-gray-100 rounded-3xl overflow-hidden hover:shadow-[0_24px_64px_rgba(11,31,58,0.13)] hover:-translate-y-2 transition-all duration-400 group flex flex-col"
              >
                {/* ── PHOTO / AVATAR AREA ── */}
                <div className="relative overflow-hidden flex-shrink-0" style={{ height: '360px' }}>
                  {member.photo ? (
                    <>
                      {/* Real photo — per-member focus point keeps face visible */}
                      <img
                        src={member.photo}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                        style={{ objectPosition: photoFocus[member.name] || 'center 20%' }}
                        draggable="false"
                      />
                      {/* Gradient — completely transparent top half, dark only at bottom */}
                      <div
                        className="absolute inset-0"
                        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 50%, rgba(7,21,38,0.7) 80%, rgba(7,21,38,0.92) 100%)' }}
                      />
                      {/* Name + role at bottom */}
                      <div className="absolute bottom-0 left-0 right-0 px-5 py-5">
                        <div className="flex items-end justify-between gap-2">
                          <div>
                            <div className="font-body font-bold text-white text-lg leading-tight mb-1.5 drop-shadow-sm">{member.name}</div>
                            <div className="inline-flex items-center gap-1.5 bg-teal/80 backdrop-blur-sm text-white text-[0.6rem] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                              {member.role}
                            </div>
                          </div>
                          {/* Verified tick */}
                          <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Gradient placeholder — large, prominent */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${member.color} opacity-15`}></div>
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4"
                        style={{ background: 'linear-gradient(160deg, #f8fafc 0%, #e2e8f0 100%)' }}>
                        {/* Large avatar circle */}
                        <div
                          className={`w-32 h-32 rounded-full bg-gradient-to-br ${member.color} flex items-center justify-center shadow-2xl`}
                          style={{ fontSize: '2.5rem', color: 'white', fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                        >
                          {member.initials}
                        </div>
                      </div>
                      {/* Dark gradient at bottom */}
                      <div
                        className="absolute bottom-0 left-0 right-0 h-40"
                        style={{ background: 'linear-gradient(to top, rgba(7,21,38,0.7) 0%, rgba(7,21,38,0.3) 50%, transparent 100%)' }}
                      />
                      {/* Name + role overlay at bottom */}
                      <div className="absolute bottom-0 left-0 right-0 px-6 py-5">
                        <div className="flex items-end justify-between gap-3">
                          <div>
                            <div className="font-body font-bold text-white text-lg leading-tight mb-1">{member.name}</div>
                            <div className={`inline-flex items-center gap-1.5 backdrop-blur-sm text-white text-[0.65rem] font-bold tracking-widest uppercase px-3 py-1 rounded-full bg-gradient-to-r ${member.color} opacity-90`}>
                              {member.role}
                            </div>
                          </div>
                          {/* Camera placeholder icon */}
                          <div className="flex-shrink-0 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                              <circle cx="12" cy="13" r="4"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* ── CARD BODY ── */}
                <div className="p-6 flex flex-col flex-1">
                  {/* Bio */}
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{member.bio}</p>

                  {/* Expertise tags */}
                  <div className="flex flex-wrap gap-1.5 mb-6 flex-1">
                    {member.expertise.map(e => (
                      <span key={e} className="text-[0.68rem] font-semibold text-teal bg-teal/8 border border-teal/15 px-2.5 py-1 rounded-full">
                        {e}
                      </span>
                    ))}
                  </div>

                  {/* WhatsApp CTA button — text only, clean */}
                  <a
                    href={`${WHATSAPP_URL}?text=Hi, I'd like to book a consultation with ${encodeURIComponent(member.name)} at Travizon`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white text-sm font-bold py-3 rounded-2xl transition-colors duration-200 shadow-[0_4px_14px_rgba(37,211,102,0.3)]"
                  >
                    {/* WhatsApp logo SVG — clean, no clutter */}
                    <svg className="w-4 h-4 fill-white flex-shrink-0" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Book Consultation
                  </a>
                </div>
              </div>
            ))}
          </div>

        </div>
        
      </section>

      {/* ── CULTURE ── */}
      <section className="py-16 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="section-tag" style={{background:'rgba(201,168,76,0.15)',color:'#E8C97A'}}>Our Culture</span>
              <h2 className="font-display text-4xl text-white font-semibold mt-1 mb-5">We're Honest. Even When It's Uncomfortable.</h2>
              <p className="text-white/55 leading-relaxed mb-6">If your profile isn't ready, we tell you. If a country isn't realistic for your budget, we tell you. If a refusal history will be a challenge, we tell you — and then we figure out how to fix it.</p>
              <p className="text-white/55 leading-relaxed">Our culture is built on radical honesty because we've seen the damage done by consultancies that over-promise and under-deliver. Our clients trust us because we've never let them walk into a rejection we could have prevented.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { n: '500+', l: 'Visas Approved' },
                { n: '98%',  l: 'Approval Rate' },
                { n: '35+',  l: 'Countries Covered' },
                { n: '7+',   l: 'Years Experience' },
              ].map(({ n, l }) => (
                <div key={l} className="bg-white/[0.06] border border-white/10 rounded-2xl p-6 text-center hover:border-teal/40 transition-all">
                  <div className="font-display text-4xl text-teal-light font-semibold mb-1">{n}</div>
                  <div className="text-white/45 text-sm">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection title="Ready to Work With Our Team?" subtitle="Book a free consultation and meet the counselor best suited to your specific goals." setPage={setPage} />
    </div>
  );
}
